import type { CreateProviderInput } from '@emailforge/schemas';
import { Button, Chip, Input, Select } from '@emailforge/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icon } from '@/components/ui/icons';
import { Logo } from '@/components/ui/Logo';
import { useLazyAutoDetectBrandingQuery, useUpdateBrandingMutation } from '@/features/branding/brandingApi';
import { useUpdateOrgMutation } from '@/features/organizations/organizationsApi';
import { useCreateProviderMutation } from '@/features/providers/providersApi';
import { useSendTestMutation } from '@/features/sends/sendsApi';
import { useCreateTemplateMutation, useListGalleryQuery } from '@/features/templates/templatesApi';

type Step = { key: string; title: string; description: string };
const STEPS: Step[] = [
  { key: 'workspace', title: 'Workspace', description: 'Name your workspace and pick a subdomain.' },
  { key: 'brand', title: 'Brand', description: 'Pick your brand colors and typography.' },
  { key: 'template', title: 'Starter template', description: 'Pick a starting point from our gallery.' },
  { key: 'smtp', title: 'SMTP provider', description: 'Connect the provider you already use to send emails.' },
  { key: 'send', title: 'First send', description: 'Send your first test email and hit the ground running.' },
];

const USE_CASES = [
  { k: 'trans', l: 'Transactional emails', d: 'Order confirmations, invoices, password resets' },
  { k: 'lifecycle', l: 'Lifecycle emails', d: 'Onboarding drips, welcome flows' },
  { k: 'marketing', l: 'Marketing', d: 'Newsletters, campaigns, promotions' },
  { k: 'all', l: 'All of the above', d: '' },
];

const BRAND_PRESETS = [
  { name: 'Ember', colors: { primary: '#ea5a24', accent: '#ffb347', ink: '#1a1a1a' }, font: 'Fraunces' },
  { name: 'Spark', colors: { primary: '#1e9c6b', accent: '#71e2af', ink: '#0f2a1f' }, font: 'Inter' },
  { name: 'Aqua', colors: { primary: '#1d6fb8', accent: '#5ec1f5', ink: '#0a1e2d' }, font: 'IBM Plex Sans' },
  { name: 'Slate', colors: { primary: '#2b2b2b', accent: '#9a9a9a', ink: '#0f0f0f' }, font: 'JetBrains Mono' },
];

const PROVIDERS = [
  { k: 'resend', n: 'Resend', badge: 'Fastest to connect' },
  { k: 'sendgrid', n: 'SendGrid' },
  { k: 'postmark', n: 'Postmark' },
  { k: 'mailgun', n: 'Mailgun' },
  { k: 'ses', n: 'Amazon SES' },
  { k: 'smtp_generic', n: 'Generic SMTP' },
] as const;

type ProviderKey = (typeof PROVIDERS)[number]['k'];

type WizardState = {
  workspaceName: string;
  subdomain: string;
  useCase: string;
  /** Index into BRAND_PRESETS, or `-1` when the user picked the auto-detected brand. */
  brandPreset: number;
  autoDetectedBrand: { logoUrl: string | null; primaryColor: string | null; domain: string } | null;
  galleryId: string | null;
  providerType: ProviderKey;
  apiKey: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPass: string;
  sesAccessKey: string;
  sesSecret: string;
  sesRegion: string;
  mailgunDomain: string;
  mailgunRegion: 'us' | 'eu';
  testEmail: string;
  createdTemplateId: string | null;
  sendResult: 'idle' | 'sending' | 'sent';
};

const DEFAULT_STATE: WizardState = {
  workspaceName: '',
  subdomain: '',
  useCase: 'trans',
  brandPreset: 0,
  autoDetectedBrand: null,
  galleryId: null,
  providerType: 'resend',
  apiKey: '',
  smtpHost: '',
  smtpPort: '587',
  smtpUser: '',
  smtpPass: '',
  sesAccessKey: '',
  sesSecret: '',
  sesRegion: 'us-east-1',
  mailgunDomain: '',
  mailgunRegion: 'us',
  testEmail: '',
  createdTemplateId: null,
  sendResult: 'idle',
};

export function OnboardingWizard() {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<WizardState>(DEFAULT_STATE);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const [updateOrg, orgMut] = useUpdateOrgMutation();
  const [updateBranding, brandMut] = useUpdateBrandingMutation();
  const [createTemplate, tmplMut] = useCreateTemplateMutation();
  const [createProvider, provMut] = useCreateProviderMutation();
  const [sendTest, sendMut] = useSendTestMutation();

  const busy =
    orgMut.isLoading || brandMut.isLoading || tmplMut.isLoading || provMut.isLoading || sendMut.isLoading;

  const progress = Math.round(((step + 1) / STEPS.length) * 100);
  const currentStep = STEPS[step];
  if (!currentStep) return null;

  const patch = (p: Partial<WizardState>) => setState((s) => ({ ...s, ...p }));

  const handleNext = async () => {
    setError(null);
    try {
      if (step === 0) {
        if (!state.workspaceName.trim()) {
          setError('Workspace name is required.');
          return;
        }
        if (state.subdomain && !/^[a-z0-9-]{3,32}$/.test(state.subdomain)) {
          setError('Subdomain must be 3–32 chars, lowercase letters, digits or hyphens.');
          return;
        }
        await updateOrg({
          name: state.workspaceName.trim(),
          ...(state.subdomain ? { slug: state.subdomain } : {}),
        }).unwrap();
      } else if (step === 1) {
        if (state.brandPreset === -1 && state.autoDetectedBrand) {
          const auto = state.autoDetectedBrand;
          await updateBranding({
            ...(auto.logoUrl ? { logoUrl: auto.logoUrl } : {}),
            colors: auto.primaryColor
              ? { primary: auto.primaryColor, accent: auto.primaryColor, ink: '#1a1a1a' }
              : { primary: '#ea5a24', accent: '#ffb347', ink: '#1a1a1a' },
            fonts: { heading: 'Fraunces', body: 'Inter' },
          }).unwrap();
        } else {
          const preset = BRAND_PRESETS[state.brandPreset];
          if (!preset) return;
          await updateBranding({
            colors: preset.colors,
            fonts: { heading: preset.font, body: preset.font },
          }).unwrap();
        }
      } else if (step === 2) {
        if (!state.galleryId) {
          setError('Pick a template to continue.');
          return;
        }
        const res = await createTemplate({
          name: state.workspaceName ? `${state.workspaceName} starter` : 'Starter template',
          forkFromGalleryId: state.galleryId,
        }).unwrap();
        patch({ createdTemplateId: res.id });
      } else if (step === 3) {
        const input = buildProviderInput(state);
        if (!input) {
          setError('Please fill in the provider credentials.');
          return;
        }
        await createProvider(input).unwrap();
      } else if (step === 4) {
        if (!state.createdTemplateId) {
          setError('Template not created yet — go back to step 3.');
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.testEmail)) {
          setError('Enter a valid email to send the test to.');
          return;
        }
        patch({ sendResult: 'sending' });
        await sendTest({ templateId: state.createdTemplateId, to: state.testEmail }).unwrap();
        patch({ sendResult: 'sent' });
        navigate('/app');
        return;
      }
      setStep((s) => Math.min(STEPS.length - 1, s + 1));
    } catch (e: unknown) {
      const msg =
        (e as { data?: { message?: string } })?.data?.message ??
        (e as { message?: string })?.message ??
        'Something went wrong.';
      setError(msg);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)' }}>
      <div
        style={{
          height: 56,
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
          gap: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo size={22} />
          <span style={{ fontWeight: 600, fontSize: 14 }}>Emailforge</span>
        </div>
        <div style={{ flex: 1, maxWidth: 420, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'var(--paper-4)', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background: 'var(--flame)',
                transition: 'width 200ms ease',
              }}
            />
          </div>
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            Step {step + 1} of {STEPS.length}
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <Button variant="ghost" size="sm" onClick={() => navigate('/app')}>
          Skip for now
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', flex: 1 }}>
        <aside
          style={{
            background: 'var(--paper-2)',
            borderRight: '1px solid var(--line)',
            padding: '28px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 10,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              marginBottom: 4,
            }}
          >
            Get started
          </div>

          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <div
                key={s.key}
                onClick={() => i < step && setStep(i)}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: 8,
                  borderRadius: 'var(--r-2)',
                  cursor: i < step ? 'pointer' : 'default',
                  background: active ? 'var(--paper-3)' : 'transparent',
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: done ? 'var(--spark-soft)' : active ? 'var(--flame-soft)' : 'var(--paper-3)',
                    color: done ? 'var(--spark)' : active ? 'var(--flame-2)' : 'var(--ink-3)',
                    border: `1px solid ${done ? 'var(--spark-line)' : active ? 'var(--flame-line)' : 'var(--line-soft)'}`,
                    fontSize: 11,
                    fontWeight: 600,
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  {done ? <Icon.check size={12} /> : i + 1}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: active ? 550 : 500,
                      color: active ? 'var(--ink)' : 'var(--ink-2)',
                    }}
                  >
                    {s.title}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.4 }}>
                    {s.description}
                  </div>
                </div>
              </div>
            );
          })}

          <div
            style={{
              marginTop: 'auto',
              padding: 14,
              background: 'var(--flame-soft)',
              border: '1px solid var(--flame-line)',
              borderRadius: 'var(--r-3)',
              fontSize: 12,
              color: 'var(--flame-2)',
              display: 'flex',
              gap: 10,
            }}
          >
            <Icon.bolt size={14} />
            <span>
              <strong>Time-to-value tip:</strong> you can skip everything and still send your first email. Come back to
              this anytime.
            </span>
          </div>
        </aside>

        <section style={{ padding: '40px 48px', maxWidth: 760 }}>
          <div
            className="mono"
            style={{
              fontSize: 10.5,
              color: 'var(--flame-2)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              marginBottom: 8,
            }}
          >
            Step {step + 1}
          </div>
          <h1 className="display" style={{ fontSize: 36, margin: '0 0 8px', letterSpacing: -0.02 }}>
            {currentStep.title}
          </h1>
          <p style={{ fontSize: 15, color: 'var(--ink-2)', marginBottom: 32 }}>{currentStep.description}</p>

          {step === 0 && <WorkspaceStep state={state} patch={patch} />}
          {step === 1 && <BrandStep state={state} patch={patch} />}
          {step === 2 && <TemplateStep state={state} patch={patch} />}
          {step === 3 && <SMTPStep state={state} patch={patch} />}
          {step === 4 && <FirstSendStep state={state} patch={patch} />}

          {error && (
            <div
              style={{
                marginTop: 20,
                padding: 12,
                background: 'var(--flame-soft)',
                border: '1px solid var(--flame-line)',
                borderRadius: 'var(--r-2)',
                color: 'var(--flame-2)',
                fontSize: 13,
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <Icon.bolt size={14} /> {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 40 }}>
            {step > 0 && (
              <Button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={busy}>
                Back
              </Button>
            )}
            <div style={{ flex: 1 }} />
            <Button variant="flame" onClick={handleNext} disabled={busy}>
              {busy ? 'Saving…' : step === STEPS.length - 1 ? 'Send test & finish' : 'Continue'}
              <Icon.arrow size={14} />
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

function buildProviderInput(s: WizardState): CreateProviderInput | null {
  const displayName = `${s.providerType} · onboarding`;
  switch (s.providerType) {
    case 'resend':
      if (!s.apiKey) return null;
      return { providerType: 'resend', displayName, apiKey: s.apiKey };
    case 'sendgrid':
      if (!s.apiKey) return null;
      return { providerType: 'sendgrid', displayName, apiKey: s.apiKey };
    case 'postmark':
      if (!s.apiKey) return null;
      return { providerType: 'postmark', displayName, serverToken: s.apiKey };
    case 'mailgun':
      if (!s.apiKey || !s.mailgunDomain) return null;
      return {
        providerType: 'mailgun',
        displayName,
        apiKey: s.apiKey,
        domain: s.mailgunDomain,
        region: s.mailgunRegion,
      };
    case 'ses':
      if (!s.sesAccessKey || !s.sesSecret) return null;
      return {
        providerType: 'ses',
        displayName,
        accessKeyId: s.sesAccessKey,
        secretAccessKey: s.sesSecret,
        region: s.sesRegion,
      };
    case 'smtp_generic':
      if (!s.smtpHost || !s.smtpUser || !s.smtpPass) return null;
      return {
        providerType: 'smtp_generic',
        displayName,
        host: s.smtpHost,
        port: Number(s.smtpPort) || 587,
        username: s.smtpUser,
        password: s.smtpPass,
        secure: Number(s.smtpPort) === 465,
      };
    default:
      return null;
  }
}

type StepProps = { state: WizardState; patch: (p: Partial<WizardState>) => void };

function WorkspaceStep({ state, patch }: StepProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 480 }}>
      <Field label="Workspace name">
        <Input
          
          style={{ height: 40 }}
          placeholder="Acme Inc"
          value={state.workspaceName}
          onChange={(e) => {
            const v = e.target.value;
            patch({
              workspaceName: v,
              subdomain: state.subdomain || slugify(v),
            });
          }} />
      </Field>
      <Field label="Subdomain">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input
            
            style={{ height: 40 }}
            placeholder="acme-inc"
            value={state.subdomain}
            onChange={(e) => patch({ subdomain: e.target.value.toLowerCase() })} />
          <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            .emailforge.app
          </span>
        </div>
      </Field>
      <Field label="What do you send?">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {USE_CASES.map((opt) => {
            const active = state.useCase === opt.k;
            return (
              <label
                key={opt.k}
                style={{
                  padding: 12,
                  border: `1px solid ${active ? 'var(--flame-line)' : 'var(--line)'}`,
                  borderRadius: 'var(--r-3)',
                  cursor: 'pointer',
                  background: active ? 'var(--flame-soft)' : 'var(--paper)',
                }}
              >
                <input
                  type="radio"
                  name="use-case"
                  checked={active}
                  onChange={() => patch({ useCase: opt.k })}
                  style={{ marginRight: 8 }}
                />
                <span style={{ fontSize: 13, fontWeight: 550 }}>{opt.l}</span>
                {opt.d && (
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.4 }}>{opt.d}</div>
                )}
              </label>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

function BrandStep({ state, patch }: StepProps) {
  const [domainInput, setDomainInput] = useState('');
  const [trigger, autoMut] = useLazyAutoDetectBrandingQuery();

  const runDetect = async () => {
    if (!domainInput.trim()) return;
    try {
      const res = await trigger(domainInput.trim()).unwrap();
      patch({
        autoDetectedBrand: {
          logoUrl: res.logoUrl,
          primaryColor: res.primaryColor,
          domain: res.domain,
        },
        brandPreset: -1,
      });
    } catch {
      // Stay quiet — the user will see the loading state turn off and can
      // either retry or pick a preset.
    }
  };

  return (
    <div>
      <div className="mf-card" style={{ padding: 14, marginBottom: 16, background: 'var(--paper-2)' }}>
        <div style={{ fontSize: 12, fontWeight: 550, marginBottom: 6 }}>
          Have a website? We'll borrow your logo.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                runDetect();
              }
            }}
            placeholder="acme.com"
            style={{ flex: 1 }}
          />
          <Button
            variant="flame"
            size="sm"
            onClick={runDetect}
            disabled={autoMut.isFetching || !domainInput.trim()}
          >
            {autoMut.isFetching ? 'Detecting…' : 'Auto-detect'}
          </Button>
        </div>
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.5 }}>
          We pull the logo via Clearbit's free directory and the primary color from the page's
          <code style={{ margin: '0 4px' }}>theme-color</code>meta tag. You can refine in Settings later.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
        {state.autoDetectedBrand && (
          <div
            onClick={() => patch({ brandPreset: -1 })}
            className="mf-card"
            style={{
              padding: 16,
              cursor: 'pointer',
              border: `1px solid ${state.brandPreset === -1 ? 'var(--flame)' : 'var(--line)'}`,
              background: state.brandPreset === -1 ? 'var(--flame-soft)' : 'var(--paper)',
              gridColumn: '1 / -1',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              {state.autoDetectedBrand.logoUrl && (
                <img
                  src={state.autoDetectedBrand.logoUrl}
                  alt={`${state.autoDetectedBrand.domain} logo`}
                  style={{ width: 36, height: 36, borderRadius: 6, background: 'white', objectFit: 'contain' }}
                  onError={(e) => {
                    // Clearbit returns 404 for unknown domains — hide the broken-image icon.
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{state.autoDetectedBrand.domain}</div>
                <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase' }}>
                  Auto-detected
                </div>
              </div>
              {state.brandPreset === -1 && (
                <Chip variant="flame" dot={false}>
                  <Icon.check size={10} /> Selected
                </Chip>
              )}
            </div>
            {state.autoDetectedBrand.primaryColor && (
              <div style={{ display: 'flex', gap: 6 }}>
                <div
                  style={{
                    flex: 1,
                    height: 32,
                    borderRadius: 'var(--r-2)',
                    background: state.autoDetectedBrand.primaryColor,
                  }}
                />
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', alignSelf: 'center', padding: 8 }}>
                  {state.autoDetectedBrand.primaryColor}
                </div>
              </div>
            )}
          </div>
        )}
        {BRAND_PRESETS.map((preset, i) => {
          const active = state.brandPreset === i;
          return (
            <div
              key={preset.name}
              onClick={() => patch({ brandPreset: i })}
              className="mf-card"
              style={{
                padding: 16,
                cursor: 'pointer',
                border: `1px solid ${active ? 'var(--flame)' : 'var(--line)'}`,
                background: active ? 'var(--flame-soft)' : 'var(--paper)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{preset.name}</div>
                {active && (
                  <Chip variant="flame" dot={false}>
                    <Icon.check size={10} /> Selected
                  </Chip>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {Object.values(preset.colors).map((c) => (
                  <div key={c} style={{ flex: 1, height: 38, borderRadius: 'var(--r-2)', background: c }} />
                ))}
              </div>
              <div
                className="mono"
                style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
              >
                Font · {preset.font}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TemplateStep({ state, patch }: StepProps) {
  const { data, isLoading, error } = useListGalleryQuery();

  const grouped = useMemo(() => data ?? [], [data]);

  if (isLoading) {
    return <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading gallery…</div>;
  }

  if (error || grouped.length === 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
        Gallery is empty. You can skip this step and create a template later.
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {grouped.map((item) => {
        const active = state.galleryId === item.id;
        return (
          <div
            key={item.id}
            onClick={() => patch({ galleryId: item.id })}
            className="mf-card"
            style={{
              padding: 12,
              cursor: 'pointer',
              border: active ? '1px solid var(--flame)' : '1px solid var(--line)',
              background: active ? 'var(--flame-soft)' : 'var(--paper)',
            }}
          >
            <div
              style={{
                height: 120,
                borderRadius: 'var(--r-2)',
                background: item.previewUrl ? `center/cover url(${item.previewUrl})` : 'var(--paper-3)',
                marginBottom: 10,
              }}
            />
            <div style={{ fontSize: 13, fontWeight: 550 }}>{item.name}</div>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 2 }}>
              {item.category} · {item.forkedCount} forks
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SMTPStep({ state, patch }: StepProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {PROVIDERS.map((p) => {
          const active = state.providerType === p.k;
          return (
            <div
              key={p.k}
              onClick={() => patch({ providerType: p.k as ProviderKey })}
              className="mf-card"
              style={{
                padding: 16,
                cursor: 'pointer',
                border: active ? '1px solid var(--flame)' : '1px solid var(--line)',
                background: active ? 'var(--flame-soft)' : 'var(--paper)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{p.n}</div>
                {'badge' in p && p.badge && (
                  <Chip variant="flame">{p.badge}
                  </Chip>
                )}
              </div>
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                API key · DNS auto-verify
              </div>
            </div>
          );
        })}
      </div>

      <ProviderCredentialsForm state={state} patch={patch} />
    </div>
  );
}

function ProviderCredentialsForm({ state, patch }: StepProps) {
  if (state.providerType === 'resend' || state.providerType === 'sendgrid' || state.providerType === 'postmark') {
    return (
      <Field label={state.providerType === 'postmark' ? 'Server token' : 'API key'}>
        <Input
          
          style={{ height: 40 }}
          type="password"
          placeholder="re_...  or  SG....  or  pmak_..."
          value={state.apiKey}
          onChange={(e) => patch({ apiKey: e.target.value })} />
      </Field>
    );
  }

  if (state.providerType === 'mailgun') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
        <Field label="API key">
          <Input
            
            style={{ height: 40 }}
            type="password"
            value={state.apiKey}
            onChange={(e) => patch({ apiKey: e.target.value })} />
        </Field>
        <Field label="Sending domain">
          <Input
            
            style={{ height: 40 }}
            placeholder="mg.example.com"
            value={state.mailgunDomain}
            onChange={(e) => patch({ mailgunDomain: e.target.value })} />
        </Field>
        <Field label="Region">
          <Select
            
            style={{ height: 40 }}
            value={state.mailgunRegion}
            onChange={(e) => patch({ mailgunRegion: e.target.value as 'us' | 'eu' })}>
            <option value="us">US</option>
            <option value="eu">EU</option>
          </Select>
        </Field>
      </div>
    );
  }

  if (state.providerType === 'ses') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
        <Field label="Access key ID">
          <Input
            
            style={{ height: 40 }}
            value={state.sesAccessKey}
            onChange={(e) => patch({ sesAccessKey: e.target.value })} />
        </Field>
        <Field label="Secret access key">
          <Input
            
            style={{ height: 40 }}
            type="password"
            value={state.sesSecret}
            onChange={(e) => patch({ sesSecret: e.target.value })} />
        </Field>
        <Field label="Region">
          <Input
            
            style={{ height: 40 }}
            value={state.sesRegion}
            onChange={(e) => patch({ sesRegion: e.target.value })} />
        </Field>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
      <Field label="Host">
        <Input
          
          style={{ height: 40 }}
          placeholder="smtp.example.com"
          value={state.smtpHost}
          onChange={(e) => patch({ smtpHost: e.target.value })} />
      </Field>
      <Field label="Port">
        <Input
          
          style={{ height: 40 }}
          value={state.smtpPort}
          onChange={(e) => patch({ smtpPort: e.target.value })} />
      </Field>
      <Field label="Username">
        <Input
          
          style={{ height: 40 }}
          value={state.smtpUser}
          onChange={(e) => patch({ smtpUser: e.target.value })} />
      </Field>
      <Field label="Password">
        <Input
          
          style={{ height: 40 }}
          type="password"
          value={state.smtpPass}
          onChange={(e) => patch({ smtpPass: e.target.value })} />
      </Field>
    </div>
  );
}

function FirstSendStep({ state, patch }: StepProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 480 }}>
      <Field label="Send test to">
        <Input
          
          style={{ height: 40 }}
          type="email"
          placeholder="you@company.com"
          value={state.testEmail}
          onChange={(e) => patch({ testEmail: e.target.value })} />
      </Field>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
        We'll render the template you forked and hand it off to your provider. You'll see delivery events in your
        dashboard within seconds.
      </div>
      {state.sendResult === 'sent' && (
        <div
          style={{
            padding: 16,
            background: 'var(--spark-soft)',
            border: '1px solid var(--spark-line)',
            borderRadius: 'var(--r-3)',
            color: 'oklch(0.40 0.14 148)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
          }}
        >
          <Icon.check size={16} />
          <span>
            <strong>Test email queued.</strong> Check your inbox — should arrive within 20 seconds.
          </span>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
}
